import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  BottomSheet,
  Button,
  Card,
  DropdownSelect,
  NumPad,
  SegmentedControl,
  useToast,
  type DropdownOption,
} from '../../components';
import { useActions, useAppData } from '../../data';
import type { TransactionType } from '../../data/types';
import { parseBulkText, type ParsedDraft } from '../../lib/bulkPasteParser';
import { formatCurrency } from '../../lib/currency';

const EXAMPLE = `Coffee 5.50 Dining
Gas 42 Transport
Walmart 87.23
-200 Paycheck Income`;

type Props = {
  open: boolean;
  onClose: () => void;
};

type DraftRow = ParsedDraft & {
  /** UI-mutable copy of the parsed values. */
  description: string;
  amount: number | null;
  categoryId: string | null;
  type: TransactionType;
  include: boolean;
  /** Sub-sheet for editing this row's amount via NumPad. */
};

/**
 * Bulk Add Transactions — paste a freeform list, parse one transaction
 * per line, then review/edit each draft before committing them all in
 * a single action.
 */
export function BulkPasteSheet({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const currency = data.profile.currency;

  const [text, setText] = useState('');
  const [showExample, setShowExample] = useState(false);
  const [drafts, setDrafts] = useState<DraftRow[] | null>(null);
  const [editingAmountFor, setEditingAmountFor] = useState<number | null>(null);

  const reset = () => {
    setText('');
    setDrafts(null);
    setShowExample(false);
    setEditingAmountFor(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleParse = () => {
    const parsed = parseBulkText(text, data.categories);
    if (parsed.length === 0) {
      toast.show({ message: 'Nothing to parse', kind: 'warning' });
      return;
    }
    setDrafts(
      parsed.map((p) => ({
        ...p,
        // Default the editable category to the direct match, falling
        // back to the keyword suggestion. The user can still change it.
        categoryId: p.categoryId ?? p.suggestedCategoryId,
        // Invalid rows are excluded by default so a sloppy line doesn't
        // accidentally land in transactions.
        include: p.valid,
      })),
    );
  };

  const updateDraft = (i: number, patch: Partial<DraftRow>) => {
    setDrafts((prev) =>
      prev ? prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)) : prev,
    );
  };

  const includedCount = drafts
    ? drafts.filter((d) => d.include && d.valid && d.amount && d.amount > 0)
        .length
    : 0;

  const commit = () => {
    if (!drafts) return;
    const todayIso = new Date().toISOString().slice(0, 10);
    let added = 0;
    for (const d of drafts) {
      if (!d.include) continue;
      if (!d.valid) continue;
      if (!d.amount || d.amount <= 0) continue;
      if (!d.description.trim()) continue;
      try {
        actions.addTransaction({
          amount: d.amount,
          type: d.type,
          categoryId: d.categoryId ?? '',
          description: d.description.trim(),
          date: todayIso,
          source: 'manual',
        });
        added += 1;
      } catch {
        /* skip individual failures */
      }
    }
    toast.show({
      message: `Added ${added} transaction${added === 1 ? '' : 's'}`,
      kind: 'success',
    });
    close();
  };

  return (
    <BottomSheet open={open} onClose={close} title="Bulk Add Transactions">
      {drafts === null ? (
        <PasteMode
          text={text}
          setText={setText}
          showExample={showExample}
          setShowExample={setShowExample}
          onParse={handleParse}
          onClose={close}
        />
      ) : (
        <ReviewMode
          drafts={drafts}
          currency={currency}
          categories={data.categories}
          onUpdate={updateDraft}
          onAmountTap={(i) => setEditingAmountFor(i)}
          onCancel={() => setDrafts(null)}
          onCommit={commit}
          includedCount={includedCount}
        />
      )}

      {/* Amount sub-sheet — only mounts when editing a row's amount. */}
      <BottomSheet
        open={editingAmountFor !== null}
        onClose={() => setEditingAmountFor(null)}
        title="Amount"
      >
        {editingAmountFor !== null && drafts ? (
          <NumPad
            initialValue={drafts[editingAmountFor]?.amount ?? 0}
            currency={currency}
            onDone={(v) => {
              if (editingAmountFor !== null) {
                updateDraft(editingAmountFor, {
                  amount: v,
                  valid:
                    v > 0 && (drafts[editingAmountFor]?.description.trim().length ?? 0) > 0,
                  include:
                    v > 0 && (drafts[editingAmountFor]?.description.trim().length ?? 0) > 0,
                });
              }
              setEditingAmountFor(null);
            }}
          />
        ) : null}
      </BottomSheet>
    </BottomSheet>
  );
}

// ---------- Paste mode ----------

function PasteMode({
  text,
  setText,
  showExample,
  setShowExample,
  onParse,
  onClose,
}: {
  text: string;
  setText: (v: string) => void;
  showExample: boolean;
  setShowExample: (v: boolean) => void;
  onParse: () => void;
  onClose: () => void;
}) {
  return (
    <div className="px-screen-x py-3 flex flex-col gap-3">
      <p className="text-[13px] text-text-secondary">
        Paste a list. One transaction per line. We'll parse amounts and
        suggest categories.
      </p>

      <button
        type="button"
        onClick={() => setShowExample(!showExample)}
        className="inline-flex items-center gap-1 text-[12px] font-medium text-accent self-start"
      >
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`transition-transform ${showExample ? 'rotate-180' : ''}`}
        />
        {showExample ? 'Hide example' : 'Show example'}
      </button>
      {showExample ? (
        <Card flush elevation="none" className="bg-surface-secondary">
          <pre className="px-card-pad py-3 text-[12px] font-mono text-text-secondary whitespace-pre-wrap leading-relaxed">
            {EXAMPLE}
          </pre>
        </Card>
      ) : null}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        spellCheck={false}
        placeholder="Paste your list here…"
        className="rounded-button bg-surface-secondary p-3 text-[14px] font-mono text-text-primary outline-none focus:ring-2 focus:ring-accent leading-relaxed resize-y"
      />

      <div className="flex gap-2">
        <Button variant="ghost" fullWidth onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          fullWidth
          onClick={onParse}
          disabled={text.trim().length === 0}
        >
          Parse
        </Button>
      </div>
    </div>
  );
}

// ---------- Review mode ----------

function ReviewMode({
  drafts,
  currency,
  categories,
  onUpdate,
  onAmountTap,
  onCancel,
  onCommit,
  includedCount,
}: {
  drafts: DraftRow[];
  currency: string;
  categories: ReturnType<typeof useAppData>['categories'];
  onUpdate: (i: number, patch: Partial<DraftRow>) => void;
  onAmountTap: (i: number) => void;
  onCancel: () => void;
  onCommit: () => void;
  includedCount: number;
}) {
  return (
    <div className="px-screen-x py-3 flex flex-col gap-3">
      <p className="text-[13px] text-text-secondary">
        Review each draft. Skip the ones you don't want.
      </p>

      <div className="flex flex-col gap-2.5">
        {drafts.map((d, i) => (
          <DraftCard
            key={i}
            index={i}
            draft={d}
            currency={currency}
            categories={categories}
            onUpdate={onUpdate}
            onAmountTap={onAmountTap}
          />
        ))}
      </div>

      <div className="flex gap-2 sticky bottom-0 bg-surface pt-2">
        <Button variant="ghost" fullWidth onClick={onCancel}>
          Back
        </Button>
        <Button
          variant="primary"
          fullWidth
          disabled={includedCount === 0}
          onClick={onCommit}
        >
          Add {includedCount} transaction{includedCount === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
}

function DraftCard({
  index,
  draft,
  currency,
  categories,
  onUpdate,
  onAmountTap,
}: {
  index: number;
  draft: DraftRow;
  currency: string;
  categories: ReturnType<typeof useAppData>['categories'];
  onUpdate: (i: number, patch: Partial<DraftRow>) => void;
  onAmountTap: (i: number) => void;
}) {
  const filteredCategories = useMemo(
    () =>
      categories
        .filter((c) => c.type === draft.type)
        .sort((a, b) => a.order - b.order),
    [categories, draft.type],
  );
  const categoryOptions: DropdownOption[] = filteredCategories.map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const fromSuggestion =
    !!draft.suggestedCategoryId &&
    draft.categoryId === draft.suggestedCategoryId &&
    !draft.rawLine.toLowerCase().includes(
      categories
        .find((c) => c.id === draft.suggestedCategoryId)
        ?.name.toLowerCase() ?? '\0',
    );

  // If the type flips and the current category is wrong-typed, pin to
  // the first valid option so the dropdown doesn't show a stale id.
  const categoryValue =
    draft.categoryId &&
    filteredCategories.find((c) => c.id === draft.categoryId)
      ? draft.categoryId
      : filteredCategories[0]?.id ?? '';

  return (
    <Card
      elevation="sm"
      className={`p-3 rounded-card ${
        !draft.valid ? 'border border-warning/40' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={draft.include}
          onChange={(e) => onUpdate(index, { include: e.target.checked })}
          className="mt-1.5 shrink-0"
          aria-label="Include this draft"
        />
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <input
            type="text"
            value={draft.description}
            onChange={(e) =>
              onUpdate(index, {
                description: e.target.value,
                valid: e.target.value.trim().length > 0 && !!draft.amount && draft.amount > 0,
              })
            }
            placeholder="Description"
            className="rounded-button bg-surface-secondary px-3 py-2 text-[14px] text-text-primary outline-none focus:ring-2 focus:ring-accent"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onAmountTap(index)}
              className="rounded-button bg-surface-secondary px-3 py-2 text-[14px] font-mono tabular-nums text-text-primary text-left"
            >
              {draft.amount !== null
                ? formatCurrency(draft.amount, { currency })
                : 'Set amount'}
            </button>
            <SegmentedControl
              options={[
                { value: 'expense', label: 'Expense' },
                { value: 'income', label: 'Income' },
              ]}
              value={draft.type}
              onChange={(v) => {
                onUpdate(index, {
                  type: v,
                  // Clear category if it doesn't match the new type.
                  categoryId:
                    categories.find(
                      (c) => c.id === draft.categoryId && c.type === v,
                    )?.id ?? null,
                });
              }}
            />
          </div>
          <DropdownSelect
            options={categoryOptions}
            value={categoryValue}
            onChange={(v) => onUpdate(index, { categoryId: v })}
          />
          {fromSuggestion ? (
            <span className="text-[11px] text-text-tertiary">
              Suggested from merchant
            </span>
          ) : null}
          {draft.warning ? (
            <span className="text-[12px] text-warning">
              {draft.warning}
            </span>
          ) : null}
          <span className="text-[11px] text-text-tertiary truncate">
            From: {draft.rawLine}
          </span>
        </div>
      </div>
    </Card>
  );
}
